import { useState, useEffect } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody,
} from '@/components/ui/dialog'
import { useNavVisibilityStore, DEFAULT_HIDDEN_ITEMS } from '@/stores/navVisibilityStore'
import {
  NAV_GROUPS, ADVANCED_GROUPS, DOMAIN_COLORS,
  type NavItem, type AdvancedGroup, type NavGroup,
} from './sidebarNav'

interface SidebarConfigDialogProps {
  open: boolean
  onClose: () => void
}

function SortableItem({
  item, isHidden, onToggle,
}: { item: NavItem; isHidden: boolean; onToggle: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id })
  const Icon = item.icon
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-3 h-10 px-3 rounded-lg select-none ${
        isDragging ? 'opacity-50 bg-bg-secondary' : !isHidden ? 'bg-bg-card shadow-[0_0_0_1px_var(--color-border)]' : ''
      }`}
    >
      <input
        type="checkbox"
        checked={!isHidden}
        onChange={onToggle}
        className="w-4 h-4 rounded flex-shrink-0 cursor-pointer"
        style={{ accentColor: 'var(--color-business)' }}
      />
      <Icon className="w-4 h-4 flex-shrink-0 text-text-muted" />
      <span className={`text-sm flex-1 ${isHidden ? 'text-text-muted' : 'text-text'}`}>{item.label}</span>
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-text-muted hover:text-text p-0.5 rounded"
        title="Drag to reorder"
      >
        <GripVertical className="w-3.5 h-3.5" />
      </div>
    </div>
  )
}

function ConfigSection({
  groupId, label, domainColor, items, draftHidden, onToggle, onReorder,
}: {
  groupId: string
  label: string
  domainColor?: string
  items: NavItem[]
  draftHidden: string[]
  onToggle: (id: string) => void
  onReorder: (groupId: string, newItems: NavItem[]) => void
}) {
  const visibleCount = items.filter(i => !draftHidden.includes(i.id)).length

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = items.findIndex(i => i.id === active.id)
    const newIndex = items.findIndex(i => i.id === over.id)
    onReorder(groupId, arrayMove(items, oldIndex, newIndex))
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 px-3 mb-2">
        {domainColor && (
          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: domainColor }} />
        )}
        <span className="text-sm font-semibold text-text">{label}</span>
        <span className="text-xs text-text-muted">({visibleCount} active)</span>
      </div>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {items.map(item => (
              <SortableItem
                key={item.id}
                item={item}
                isHidden={draftHidden.includes(item.id)}
                onToggle={() => onToggle(item.id)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}

type DraftOrder = Record<string, NavItem[]>

function buildDefaultOrder(): DraftOrder {
  const out: DraftOrder = {}
  for (const g of NAV_GROUPS) {
    if (g.label) out[g.id] = [...g.items]
  }
  for (const g of ADVANCED_GROUPS) {
    out[g.id] = [...g.items]
  }
  return out
}

function buildOrderFromStore(
  storedOrder: Record<string, string[]>,
  groups: NavGroup[],
  advGroups: AdvancedGroup[],
): DraftOrder {
  const out: DraftOrder = {}

  function applyOrder(id: string, items: NavItem[]) {
    const order = storedOrder[id]
    if (!order || order.length === 0) { out[id] = [...items]; return }
    const byId = Object.fromEntries(items.map(i => [i.id, i]))
    out[id] = [
      ...order.map(oid => byId[oid]).filter(Boolean) as NavItem[],
      ...items.filter(i => !order.includes(i.id)),
    ]
  }

  for (const g of groups) {
    if (g.label) applyOrder(g.id, g.items)
  }
  for (const g of advGroups) {
    applyOrder(g.id, g.items)
  }
  return out
}

export function SidebarConfigDialog({ open, onClose }: SidebarConfigDialogProps) {
  const { hiddenItems, itemOrder, setHiddenItems, setItemOrder } = useNavVisibilityStore()

  const [draftHidden, setDraftHidden] = useState<string[]>([])
  const [draftOrder, setDraftOrder] = useState<DraftOrder>(buildDefaultOrder)

  useEffect(() => {
    if (!open) return
    setDraftHidden([...hiddenItems])
    setDraftOrder(buildOrderFromStore(itemOrder, NAV_GROUPS, ADVANCED_GROUPS))
  }, [open])

  function toggleItem(id: string) {
    setDraftHidden(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  function reorderGroup(groupId: string, newItems: NavItem[]) {
    setDraftOrder(prev => ({ ...prev, [groupId]: newItems }))
  }

  function handleSave() {
    setHiddenItems(draftHidden)
    const order: Record<string, string[]> = {}
    for (const [gid, items] of Object.entries(draftOrder)) {
      order[gid] = items.map(i => i.id)
    }
    setItemOrder(order)
    onClose()
  }

  function handleReset() {
    setDraftHidden([...DEFAULT_HIDDEN_ITEMS])
    setDraftOrder(buildDefaultOrder())
  }

  const configGroups = NAV_GROUPS.filter(g => g.label)

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="flex flex-col p-0 overflow-hidden" style={{ maxHeight: '80vh' }}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-warm-200 flex-shrink-0">
          <DialogTitle>Configure Navigation</DialogTitle>
          <DialogDescription>Choose which items appear and drag to reorder</DialogDescription>
        </DialogHeader>

        <DialogBody className="flex-1 overflow-y-auto px-4 py-4">
          {configGroups.map(group => (
            <ConfigSection
              key={group.id}
              groupId={group.id}
              label={group.label!}
              domainColor={group.domain ? DOMAIN_COLORS[group.domain] : undefined}
              items={draftOrder[group.id] ?? group.items}
              draftHidden={draftHidden}
              onToggle={toggleItem}
              onReorder={reorderGroup}
            />
          ))}

          <div className="border-t border-warm-200 pt-4 mt-2">
            <div className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-widest text-text-muted">
              More Tools
            </div>
            {ADVANCED_GROUPS.map(group => (
              <ConfigSection
                key={group.id}
                groupId={group.id}
                label={group.label}
                items={draftOrder[group.id] ?? group.items}
                draftHidden={draftHidden}
                onToggle={toggleItem}
                onReorder={reorderGroup}
              />
            ))}
          </div>
        </DialogBody>

        <div className="px-6 py-4 border-t border-warm-200 flex-shrink-0 flex items-center justify-between">
          <button
            onClick={handleReset}
            className="text-sm text-text-muted hover:text-text underline underline-offset-2 transition-colors"
          >
            Reset to defaults
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg text-text hover:bg-warm-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 text-sm rounded-lg font-medium text-white transition-opacity hover:opacity-90"
              style={{ background: 'var(--color-business)' }}
            >
              Save
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
